import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import axios from 'axios';
import {
  Bot,
  X,
  Send,
  Search,
  Sun,
  Moon,
  Grid3X3,
  List,
  Table2,
  LayoutGrid,
  FileText,
  BarChart3,
  Shield,
  Settings,
  Sparkles,
  Zap,
  ChevronRight,
  MessageCircle,
  HelpCircle,
  Home,
  Filter,
  Upload,
  Download,
  Clock,
  User,
  File,
  FileImage,
  Eye,
  ThumbsUp,
  Lightbulb,
  ArrowRight
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Suggestion chips for follow-up actions
const SUGGESTION_CHIPS = {
  greeting: [
    { label: '🔍 Search documents', command: 'search' },
    { label: '📊 View Analytics', command: 'go to analytics' },
    { label: '🌙 Toggle theme', command: 'toggle theme' },
    { label: '❓ Show help', command: 'help' }
  ],
  afterSearch: [
    { label: '🔄 New search', command: 'search' },
    { label: '📋 List view', command: 'list view' },
    { label: '🏠 Go home', command: 'go home' }
  ],
  afterViewChange: [
    { label: '🔍 Search docs', command: 'search' },
    { label: '📤 Upload', command: 'upload document' },
    { label: '📊 Analytics', command: 'go to analytics' }
  ],
  afterNavigation: [
    { label: '🏠 Dashboard', command: 'go home' },
    { label: '🔍 Search', command: 'search' },
    { label: '❓ Help', command: 'help' }
  ],
  documents: [
    { label: '👁️ Preview', command: 'preview' },
    { label: '📥 Download', command: 'download' },
    { label: '📋 List view', command: 'list view' }
  ]
};

// Greeting patterns and responses
const GREETING_PATTERNS = [
  { pattern: /^(hi|hello|hey|howdy|hola|greetings|good\s*(morning|afternoon|evening|day))[\s!.,]*$/i, type: 'greeting' },
  { pattern: /^(what'?s?\s*up|sup|yo)[\s!.,]*$/i, type: 'casual_greeting' },
  { pattern: /^(how\s*are\s*you|how'?s?\s*it\s*going|how\s*do\s*you\s*do)[\s!.,?]*$/i, type: 'how_are_you' },
  { pattern: /^(thanks?|thank\s*you|thx|ty)[\s!.,]*$/i, type: 'thanks' },
  { pattern: /^(bye|goodbye|see\s*you|later|cya)[\s!.,]*$/i, type: 'goodbye' },
  { pattern: /^(ok|okay|alright|got\s*it|understood|cool|great|nice|awesome|perfect)[\s!.,]*$/i, type: 'acknowledgment' }
];

const GREETING_RESPONSES = {
  greeting: [
    "Hello! 👋 I'm your eTMF Vault Assistant. How can I help you today?",
    "Hi there! 😊 Ready to help you manage your documents. What would you like to do?",
    "Hey! 👋 I'm here to assist you. You can search documents, change views, navigate pages, or just ask me anything!",
    "Greetings! 🌟 I'm your intelligent document assistant. How may I assist you?"
  ],
  casual_greeting: [
    "Hey! What's up? 😎 Need help finding something?",
    "Yo! 👋 Ready to help. What do you need?",
    "What's up! 🚀 Let me know how I can assist you today."
  ],
  how_are_you: [
    "I'm doing great, thanks for asking! 😊 I'm always ready to help you with your documents. What can I do for you?",
    "I'm excellent! Running smoothly and ready to assist. 🤖 What would you like help with?",
    "All systems operational! 💪 How can I make your day easier?"
  ],
  thanks: [
    "You're welcome! 😊 Is there anything else I can help you with?",
    "Happy to help! 🌟 Let me know if you need anything else.",
    "Anytime! 👍 I'm here whenever you need me."
  ],
  goodbye: [
    "Goodbye! 👋 Have a great day! I'll be here when you need me.",
    "See you later! 🌟 Don't hesitate to come back if you need help.",
    "Take care! 😊 Your documents are in good hands."
  ],
  acknowledgment: [
    "Great! 👍 What would you like to do next?",
    "Awesome! 🎉 Need help with anything else?",
    "Perfect! Let me know if you need anything more. 😊"
  ]
};

// Command patterns for natural language processing
const COMMAND_PATTERNS = [
  // Theme commands
  { pattern: /(?:switch|change|toggle|set|turn\s*on)?\s*(?:to\s+)?(?:dark|night)\s*(?:mode|theme)?/i, action: 'dark_mode' },
  { pattern: /(?:switch|change|toggle|set|turn\s*on)?\s*(?:to\s+)?(?:light|day|bright)\s*(?:mode|theme)?/i, action: 'light_mode' },
  { pattern: /(?:toggle|switch|change)\s*(?:the\s+)?theme/i, action: 'toggle_theme' },
  
  // View commands
  { pattern: /(?:switch|change|show|set|use)?\s*(?:to\s+)?(?:tiles?|card|grid)\s*(?:view|layout)?/i, action: 'tiles_view' },
  { pattern: /(?:switch|change|show|set|use)?\s*(?:to\s+)?list\s*(?:view|layout)?/i, action: 'list_view' },
  { pattern: /(?:switch|change|show|set|use)?\s*(?:to\s+)?(?:details?|table)\s*(?:view|layout)?/i, action: 'details_view' },
  { pattern: /(?:switch|change|show|set|use)?\s*(?:to\s+)?icons?\s*(?:view|layout)?/i, action: 'icons_view' },
  { pattern: /(?:switch|change|show|set|use)?\s*(?:to\s+)?content\s*(?:view|layout)?/i, action: 'content_view' },
  
  // Navigation commands
  { pattern: /(?:go\s+(?:to\s+)?|open\s+|show\s+|take\s+me\s+to\s+)?(?:the\s+)?analytics?(?:\s+(?:page|dashboard))?/i, action: 'go_analytics' },
  { pattern: /(?:go\s+(?:to\s+)?|open\s+|show\s+|take\s+me\s+to\s+)?(?:the\s+)?compliance(?:\s+(?:center|page))?/i, action: 'go_compliance' },
  { pattern: /(?:go\s+(?:to\s+)?|open\s+|show\s+|take\s+me\s+to\s+)?(?:the\s+)?admin(?:\s+(?:panel|page))?/i, action: 'go_admin' },
  { pattern: /(?:go\s+(?:to\s+)?|open\s+|show\s+|take\s+me\s+to\s+)?(?:the\s+)?(?:home|dashboard|main|start)/i, action: 'go_home' },
  
  // DATE-BASED SEARCH COMMANDS
  // Documents uploaded in specific month
  { pattern: /(?:documents?|files?|docs?)\s+(?:uploaded|created|added)\s+(?:in\s+)?(january|february|march|april|may|june|july|august|september|october|november|december)\s*(?:(\d{4}))?/i, action: 'search_by_month' },
  // Documents from specific quarter
  { pattern: /(?:documents?|files?|docs?)\s+(?:from|in|uploaded\s+in)\s+(?:q|quarter\s*)(\d)\s*(?:of\s+)?(\d{4})?/i, action: 'search_by_quarter' },
  // Documents from specific year
  { pattern: /(?:documents?|files?|docs?)\s+(?:from|in|uploaded\s+in)\s+(\d{4})/i, action: 'search_by_year' },
  // Last week/month/year
  { pattern: /(?:documents?|files?|docs?)\s+(?:uploaded|created|added|from)\s+(?:last|past|previous)\s+(week|month|year)/i, action: 'search_last_period' },
  // This week/month/year
  { pattern: /(?:documents?|files?|docs?)\s+(?:uploaded|created|added|from)\s+this\s+(week|month|year)/i, action: 'search_this_period' },
  // Documents uploaded today/yesterday
  { pattern: /(?:documents?|files?|docs?)\s+(?:uploaded|created|added)\s+(today|yesterday)/i, action: 'search_relative_day' },
  // Documents uploaded on specific date
  { pattern: /(?:documents?|files?|docs?)\s+(?:uploaded|created|added)\s+(?:on\s+)?(\d{1,2})(?:st|nd|rd|th)?\s*(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s*,?\s*(\d{4})?/i, action: 'search_specific_date' },
  // Documents between dates
  { pattern: /(?:documents?|files?|docs?)\s+(?:uploaded|created|added)?\s*(?:from|between)\s+(.+?)\s+(?:to|and|until)\s+(.+)/i, action: 'search_date_range' },
  // Overdue documents
  { pattern: /(?:overdue|expired|late)\s*(?:documents?|files?|docs?)/i, action: 'search_overdue' },
  // Documents due soon / expiring
  { pattern: /(?:documents?|files?|docs?)\s+(?:due|expiring|about\s+to\s+expire)\s*(?:soon|this\s+week|this\s+month)?/i, action: 'search_due_soon' },
  // Pending documents / requirements
  { pattern: /(?:what\s+)?(?:documents?|files?|docs?)\s+(?:are\s+)?(?:pending|missing|required|needed)/i, action: 'search_pending_requirements' },
  // Documents by status
  { pattern: /(?:documents?|files?|docs?)\s+(?:with\s+)?(?:status|that\s+are)\s+(draft|pending|approved|rejected|archived|in\s*review)/i, action: 'search_by_status' },
  // Documents by type
  { pattern: /(?:all|show|list|find)\s+(?:the\s+)?(protocol|consent|report|safety|monitoring|audit)\s*(?:documents?|files?|forms?)?/i, action: 'search_by_type' },
  // Study timeline / schedule
  { pattern: /(?:show|what\s+is|what'?s?)\s+(?:the\s+)?(?:study\s+)?(?:timeline|schedule|milestones?)/i, action: 'show_timeline' },
  
  // Document search commands - more flexible (moved after date patterns)
  { pattern: /(?:search|find|look\s*(?:for|up)?|show\s*me|get|where\s*(?:is|are)?|locate|fetch)\s+(?:(?:all\s+)?(?:documents?|files?|docs?)\s+)?(?:(?:named?|called|titled|about|related\s*to|containing|with)\s+)?["']?(.+?)["']?$/i, action: 'search_docs' },
  { pattern: /(?:any|show|list|display)\s+(?:documents?|files?|docs?)\s+(?:about|related\s*to|containing|with|on)\s+["']?(.+?)["']?$/i, action: 'search_docs' },
  { pattern: /(?:i\s+(?:need|want|am\s+looking\s+for))\s+(?:a\s+)?(?:document|file|doc)?\s*(?:about|on|for|related\s*to)?\s*["']?(.+?)["']?$/i, action: 'search_docs' },
  { pattern: /(?:open|show)\s+smart\s*search/i, action: 'smart_search' },
  
  // Document actions
  { pattern: /(?:show|view|display|list)\s+(?:all\s+)?(?:my\s+)?pending\s+(?:documents?|actions?|reviews?|tasks?)/i, action: 'pending_actions' },
  { pattern: /(?:what'?s?\s+)?pending(?:\s+for\s+me)?/i, action: 'pending_actions' },
  { pattern: /(?:upload|add|create)\s+(?:a\s+)?(?:new\s+)?(?:document|file|doc)/i, action: 'upload_doc' },
  { pattern: /(?:how\s+(?:do\s+i|to|can\s+i))\s+upload/i, action: 'upload_help' },
  
  // Status queries
  { pattern: /(?:how\s+many|count|total)\s+(?:documents?|files?|docs?)/i, action: 'doc_count' },
  { pattern: /(?:show|what\s+are)\s+(?:the\s+)?(?:recent|latest|new)\s+(?:documents?|files?|uploads?)/i, action: 'recent_docs' },
  
  // Help and capabilities
  { pattern: /(?:help|what\s+can\s+you\s+do|commands?|options?|features?|capabilities|\?+)$/i, action: 'help' },
  { pattern: /(?:how\s+(?:do\s+i|to|can\s+i))\s+(.+)/i, action: 'how_to' },
];

// Month name to number mapping
const MONTH_MAP = {
  january: 1, jan: 1, february: 2, feb: 2, march: 3, mar: 3,
  april: 4, apr: 4, may: 5, june: 6, jun: 6,
  july: 7, jul: 7, august: 8, aug: 8, september: 9, sep: 9,
  october: 10, oct: 10, november: 11, nov: 11, december: 12, dec: 12
};

// Global event emitter for cross-component communication
const botEvents = {
  listeners: {},
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  },
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
    return () => {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    };
  }
};

// Export for use in other components
export { botEvents };

export default function AssistantBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      type: 'bot', 
      text: "Hi! 👋 I'm your eTMF Vault Assistant. I can help you:\n\n🔍 Find documents - try \"search protocol\"\n🎨 Change themes - say \"dark mode\"\n📋 Switch views - say \"list view\"\n🧭 Navigate - say \"go to analytics\"\n\nJust type naturally or click a quick action below!",
      timestamp: new Date(),
      suggestions: SUGGESTION_CHIPS.greeting
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const { theme, toggleTheme, isDark } = useTheme();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch documents for search
  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/studies`, getAuthHeaders());
        if (response.data.length > 0) {
          const studyId = response.data[0].id;
          const docsResponse = await axios.get(`${API_URL}/api/studies/${studyId}/all-documents`, getAuthHeaders());
          setDocuments(docsResponse.data || []);
        }
      } catch (error) {
        console.log('Could not fetch documents for bot');
      }
    };
    if (isOpen) {
      fetchDocs();
    }
  }, [isOpen, getAuthHeaders]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when bot opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const addBotMessage = (text, suggestions = null, documents = null, delay = 500) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text, 
        timestamp: new Date(),
        suggestions,
        documents
      }]);
      setIsTyping(false);
    }, delay);
  };

  // Check for greeting patterns
  const checkGreeting = (text) => {
    const trimmedText = text.trim();
    for (const { pattern, type } of GREETING_PATTERNS) {
      if (pattern.test(trimmedText)) {
        const responses = GREETING_RESPONSES[type];
        return {
          isGreeting: true,
          response: responses[Math.floor(Math.random() * responses.length)],
          suggestions: SUGGESTION_CHIPS.greeting
        };
      }
    }
    return { isGreeting: false };
  };

  // Search documents locally
  const searchDocuments = (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const term = searchTerm.toLowerCase();
    const results = documents.filter(doc => {
      const nameMatch = doc.name?.toLowerCase().includes(term);
      const typeMatch = doc.document_type?.toLowerCase().includes(term);
      const descMatch = doc.description?.toLowerCase().includes(term);
      const classMatch = doc.artifact_classification?.toLowerCase().includes(term);
      return nameMatch || typeMatch || descMatch || classMatch;
    }).slice(0, 5); // Limit to 5 results
    
    return results;
  };

  // Advanced date-based search
  const searchByDate = (filterFn, description) => {
    const results = documents.filter(doc => {
      if (!doc.created_at) return false;
      try {
        const docDate = new Date(doc.created_at);
        return filterFn(docDate);
      } catch {
        return false;
      }
    }).slice(0, 8);
    
    return {
      results,
      description
    };
  };

  // Search by month
  const searchByMonth = (monthName, year) => {
    const month = MONTH_MAP[monthName.toLowerCase()];
    const targetYear = year ? parseInt(year) : new Date().getFullYear();
    
    const { results } = searchByDate(
      (date) => date.getMonth() + 1 === month && date.getFullYear() === targetYear,
      `${monthName} ${targetYear}`
    );
    return results;
  };

  // Search by quarter
  const searchByQuarter = (quarter, year) => {
    const q = parseInt(quarter);
    const targetYear = year ? parseInt(year) : new Date().getFullYear();
    const startMonth = (q - 1) * 3;
    const endMonth = startMonth + 2;
    
    const { results } = searchByDate(
      (date) => {
        const docMonth = date.getMonth();
        return docMonth >= startMonth && docMonth <= endMonth && date.getFullYear() === targetYear;
      },
      `Q${q} ${targetYear}`
    );
    return results;
  };

  // Search by year
  const searchByYear = (year) => {
    const targetYear = parseInt(year);
    const { results } = searchByDate(
      (date) => date.getFullYear() === targetYear,
      year.toString()
    );
    return results;
  };

  // Search by relative period (last/this week/month/year)
  const searchByPeriod = (period, isLast = true) => {
    const now = new Date();
    let startDate, endDate;
    
    if (period === 'week') {
      const dayOfWeek = now.getDay();
      if (isLast) {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - dayOfWeek - 7);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
      } else {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - dayOfWeek);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
      }
    } else if (period === 'month') {
      if (isLast) {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      } else {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      }
    } else if (period === 'year') {
      if (isLast) {
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31);
      } else {
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
      }
    }
    
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    const { results } = searchByDate(
      (date) => date >= startDate && date <= endDate,
      `${isLast ? 'last' : 'this'} ${period}`
    );
    return results;
  };

  // Search by status
  const searchByStatus = (status) => {
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
    const statusMap = {
      'draft': 'draft',
      'pending': 'pending_review',
      'in_review': 'in_review',
      'inreview': 'in_review',
      'approved': 'approved',
      'rejected': 'rejected',
      'archived': 'archived'
    };
    
    const targetStatus = statusMap[normalizedStatus] || normalizedStatus;
    
    return documents.filter(doc => doc.status === targetStatus).slice(0, 8);
  };

  // Search by document type
  const searchByType = (type) => {
    const typeMap = {
      'protocol': ['Protocol', 'Protocol Amendment'],
      'consent': ['Informed Consent Form'],
      'report': ['Study Report', 'Safety Report', 'Monitoring Report', 'Audit Report'],
      'safety': ['Safety Report'],
      'monitoring': ['Monitoring Report'],
      'audit': ['Audit Report']
    };
    
    const targetTypes = typeMap[type.toLowerCase()] || [type];
    
    return documents.filter(doc => 
      targetTypes.some(t => doc.document_type?.toLowerCase().includes(t.toLowerCase()))
    ).slice(0, 8);
  };

  const processCommand = (text) => {
    const trimmedText = text.trim();
    
    // First check for greetings
    const greetingCheck = checkGreeting(trimmedText);
    if (greetingCheck.isGreeting) {
      return {
        success: true,
        message: greetingCheck.response,
        suggestions: greetingCheck.suggestions
      };
    }
    
    // Find matching command pattern
    for (const { pattern, action } of COMMAND_PATTERNS) {
      const match = trimmedText.match(pattern);
      if (match) {
        return executeAction(action, match, trimmedText);
      }
    }
    
    // Try to find documents if no command matched (assume it's a search)
    const possibleSearch = searchDocuments(trimmedText);
    if (possibleSearch.length > 0) {
      botEvents.emit('search', trimmedText);
      navigate(`/dashboard?view=all&search=${encodeURIComponent(trimmedText)}`);
      return {
        success: true,
        message: `🔍 I found ${possibleSearch.length} document${possibleSearch.length > 1 ? 's' : ''} matching "${trimmedText}":`,
        documents: possibleSearch,
        suggestions: SUGGESTION_CHIPS.afterSearch
      };
    }
    
    // No pattern matched and no documents found
    return {
      success: false,
      message: `I'm not sure what you mean by "${trimmedText}". 🤔\n\nHere are some things I can help with:\n• Search documents: "find protocol" or "search consent form"\n• Change theme: "dark mode" or "light mode"\n• Change view: "list view" or "tiles view"\n• Navigate: "go to analytics"\n• Type "help" for all commands`,
      suggestions: SUGGESTION_CHIPS.greeting
    };
  };

  const executeAction = (action, match, originalText) => {
    switch (action) {
      case 'dark_mode':
        if (!isDark) toggleTheme();
        return { 
          success: true, 
          message: isDark ? "You're already in dark mode! 🌙" : "Switched to dark mode! 🌙 Easy on the eyes, isn't it?",
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'light_mode':
        if (isDark) toggleTheme();
        return { 
          success: true, 
          message: !isDark ? "You're already in light mode! ☀️" : "Switched to light mode! ☀️ Bright and clear!",
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'toggle_theme':
        toggleTheme();
        return { 
          success: true, 
          message: `Switched to ${isDark ? 'light' : 'dark'} mode! ${isDark ? '☀️' : '🌙'}`,
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'tiles_view':
        botEvents.emit('viewChange', 'tiles');
        navigate('/dashboard?view=all&viewType=tiles');
        return { 
          success: true, 
          message: "Changed to Tiles view! 🎴 Your documents are now displayed as cards.",
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'list_view':
        botEvents.emit('viewChange', 'list');
        navigate('/dashboard?view=all&viewType=list');
        return { 
          success: true, 
          message: "Changed to List view! 📋 Documents are now in a compact list format.",
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'details_view':
        botEvents.emit('viewChange', 'details');
        navigate('/dashboard?view=all&viewType=details');
        return { 
          success: true, 
          message: "Changed to Details view! 📊 Full table with all document information.",
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'icons_view':
        botEvents.emit('viewChange', 'icons');
        navigate('/dashboard?view=all&viewType=icons');
        return { 
          success: true, 
          message: "Changed to Icons view! 🔲 Compact icon grid for quick scanning.",
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'content_view':
        botEvents.emit('viewChange', 'content');
        navigate('/dashboard?view=all&viewType=content');
        return { 
          success: true, 
          message: "Changed to Content view! 📄 Expanded view with document previews.",
          suggestions: SUGGESTION_CHIPS.afterViewChange
        };
      
      case 'go_analytics':
        navigate('/analytics');
        return { 
          success: true, 
          message: "Taking you to Analytics Dashboard! 📈 Here you can see document statistics, trends, and predictive insights.",
          suggestions: SUGGESTION_CHIPS.afterNavigation
        };
      
      case 'go_compliance':
        navigate('/compliance');
        return { 
          success: true, 
          message: "Opening Compliance Center! 🛡️ Track your regulatory compliance status here.",
          suggestions: SUGGESTION_CHIPS.afterNavigation
        };
      
      case 'go_admin':
        navigate('/admin');
        return { 
          success: true, 
          message: "Navigating to Admin Panel! ⚙️ Manage users, settings, and audit logs.",
          suggestions: SUGGESTION_CHIPS.afterNavigation
        };
      
      case 'go_home':
        navigate('/dashboard?view=all');
        return { 
          success: true, 
          message: "Welcome back to the Dashboard! 🏠 All your documents are here.",
          suggestions: SUGGESTION_CHIPS.greeting
        };
      
      case 'search_docs':
        const searchTerm = match[1]?.trim();
        if (searchTerm) {
          const results = searchDocuments(searchTerm);
          botEvents.emit('search', searchTerm);
          navigate(`/dashboard?view=all&search=${encodeURIComponent(searchTerm)}`);
          
          if (results.length > 0) {
            return { 
              success: true, 
              message: `🔍 Found ${results.length} document${results.length > 1 ? 's' : ''} matching "${searchTerm}":`,
              documents: results,
              suggestions: SUGGESTION_CHIPS.afterSearch
            };
          } else {
            return { 
              success: true, 
              message: `🔍 Searching for "${searchTerm}"... I couldn't find exact matches in my quick search, but I've updated the main view to show any results.`,
              suggestions: SUGGESTION_CHIPS.afterSearch
            };
          }
        }
        return { 
          success: false, 
          message: "What would you like to search for? 🔍\n\nTry saying:\n• \"search protocol\"\n• \"find consent form\"\n• \"show me data management docs\"",
          suggestions: [
            { label: '📋 Protocol docs', command: 'search protocol' },
            { label: '📝 Consent forms', command: 'search consent' },
            { label: '📊 Data docs', command: 'search data' }
          ]
        };
      
      case 'smart_search':
        botEvents.emit('smartSearch', true);
        navigate('/dashboard?view=all&smartSearch=true');
        return { 
          success: true, 
          message: "Opening Smart Search panel! 🔎 Use advanced filters to find exactly what you need.",
          suggestions: SUGGESTION_CHIPS.afterSearch
        };
      
      case 'pending_actions':
        navigate('/dashboard?view=all');
        return { 
          success: true, 
          message: "Showing your dashboard! ⏳ Check the Pending Actions panel on the right side to see documents waiting for your review or signature.",
          suggestions: SUGGESTION_CHIPS.afterNavigation
        };
      
      case 'upload_doc':
        botEvents.emit('upload', true);
        navigate('/dashboard?view=all&upload=true');
        return { 
          success: true, 
          message: "Opening Smart Upload! 📤 You can drag and drop files or browse to upload. The system will automatically classify your document.",
          suggestions: SUGGESTION_CHIPS.afterNavigation
        };
      
      case 'upload_help':
        return {
          success: true,
          message: "To upload a document: 📤\n\n1. Click 'Smart Upload' button in the toolbar\n2. Select or drag a file\n3. The system auto-classifies it\n4. Confirm the folder and metadata\n5. Click Upload!\n\nWant me to open the upload dialog?",
          suggestions: [
            { label: '📤 Open Upload', command: 'upload document' },
            { label: '🏠 Go home', command: 'go home' }
          ]
        };
      
      case 'doc_count':
        const count = documents.length;
        return {
          success: true,
          message: `📊 You have ${count} document${count !== 1 ? 's' : ''} in the current study.\n\nWant to see them in a specific view?`,
          suggestions: [
            { label: '📋 List view', command: 'list view' },
            { label: '🎴 Tiles view', command: 'tiles view' },
            { label: '🔍 Search', command: 'search' }
          ]
        };
      
      case 'recent_docs':
        const recentDocs = [...documents]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5);
        
        if (recentDocs.length > 0) {
          return {
            success: true,
            message: "📅 Here are your most recent documents:",
            documents: recentDocs,
            suggestions: SUGGESTION_CHIPS.documents
          };
        }
        return {
          success: true,
          message: "No recent documents found. Would you like to upload one?",
          suggestions: [{ label: '📤 Upload', command: 'upload document' }]
        };
      
      case 'how_to':
        const topic = match[1]?.toLowerCase();
        if (topic.includes('search') || topic.includes('find')) {
          return {
            success: true,
            message: "To search documents: 🔍\n\n• Type \"search [term]\" - e.g., \"search protocol\"\n• Or click Smart Search for advanced filters\n• You can search by name, type, or description",
            suggestions: [
              { label: '🔍 Try search', command: 'search' },
              { label: '🔎 Smart Search', command: 'open smart search' }
            ]
          };
        }
        if (topic.includes('theme') || topic.includes('dark') || topic.includes('light')) {
          return {
            success: true,
            message: "To change theme: 🎨\n\n• Say \"dark mode\" or \"light mode\"\n• Or click the sun/moon icon in the header\n• The theme persists across sessions",
            suggestions: [
              { label: '🌙 Dark mode', command: 'dark mode' },
              { label: '☀️ Light mode', command: 'light mode' }
            ]
          };
        }
        if (topic.includes('view') || topic.includes('layout')) {
          return {
            success: true,
            message: "To change document view: 📋\n\n• Tiles view - card layout\n• List view - compact rows\n• Details view - full table\n• Icons view - small icons\n• Content view - with previews",
            suggestions: [
              { label: '🎴 Tiles', command: 'tiles view' },
              { label: '📋 List', command: 'list view' },
              { label: '📊 Details', command: 'details view' }
            ]
          };
        }
        return {
          success: true,
          message: `I'd be happy to help with "${topic}"! 🤔\n\nCould you be more specific? I can help with:\n• Searching documents\n• Changing themes\n• Switching views\n• Navigating pages\n• Uploading files`,
          suggestions: SUGGESTION_CHIPS.greeting
        };
      
      // DATE-BASED SEARCH ACTIONS
      case 'search_by_month': {
        const monthName = match[1];
        const year = match[2];
        const results = searchByMonth(monthName, year);
        const displayYear = year || new Date().getFullYear();
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📅 Found ${results.length} document${results.length > 1 ? 's' : ''} uploaded in ${monthName} ${displayYear}:`,
            documents: results,
            suggestions: [
              { label: '📅 This month', command: 'documents uploaded this month' },
              { label: '📅 Last month', command: 'documents uploaded last month' },
              { label: '🔍 New search', command: 'search' }
            ]
          };
        }
        return {
          success: true,
          message: `📅 No documents found uploaded in ${monthName} ${displayYear}. Try a different time period?`,
          suggestions: [
            { label: '📅 This month', command: 'documents uploaded this month' },
            { label: '📅 This year', command: 'documents from ' + new Date().getFullYear() }
          ]
        };
      }
      
      case 'search_by_quarter': {
        const quarter = match[1];
        const year = match[2];
        const results = searchByQuarter(quarter, year);
        const displayYear = year || new Date().getFullYear();
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📊 Found ${results.length} document${results.length > 1 ? 's' : ''} from Q${quarter} ${displayYear}:`,
            documents: results,
            suggestions: [
              { label: '📊 Q1', command: 'documents from Q1 ' + displayYear },
              { label: '📊 Q2', command: 'documents from Q2 ' + displayYear },
              { label: '📊 Q3', command: 'documents from Q3 ' + displayYear },
              { label: '📊 Q4', command: 'documents from Q4 ' + displayYear }
            ]
          };
        }
        return {
          success: true,
          message: `📊 No documents found from Q${quarter} ${displayYear}.`,
          suggestions: [
            { label: '📅 This year', command: 'documents from ' + displayYear },
            { label: '🔍 Search', command: 'search' }
          ]
        };
      }
      
      case 'search_by_year': {
        const year = match[1];
        const results = searchByYear(year);
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📆 Found ${results.length} document${results.length > 1 ? 's' : ''} from ${year}:`,
            documents: results,
            suggestions: [
              { label: '📊 Q1 ' + year, command: 'documents from Q1 ' + year },
              { label: '📊 Q2 ' + year, command: 'documents from Q2 ' + year },
              { label: '🔍 New search', command: 'search' }
            ]
          };
        }
        return {
          success: true,
          message: `📆 No documents found from ${year}.`,
          suggestions: [
            { label: '📅 ' + (parseInt(year) - 1), command: 'documents from ' + (parseInt(year) - 1) },
            { label: '📅 ' + (parseInt(year) + 1), command: 'documents from ' + (parseInt(year) + 1) }
          ]
        };
      }
      
      case 'search_last_period': {
        const period = match[1].toLowerCase();
        const results = searchByPeriod(period, true);
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📅 Found ${results.length} document${results.length > 1 ? 's' : ''} from last ${period}:`,
            documents: results,
            suggestions: [
              { label: '📅 This ' + period, command: 'documents from this ' + period },
              { label: '🔍 New search', command: 'search' }
            ]
          };
        }
        return {
          success: true,
          message: `📅 No documents uploaded last ${period}.`,
          suggestions: [
            { label: '📅 This ' + period, command: 'documents from this ' + period },
            { label: '📅 Recent docs', command: 'show recent documents' }
          ]
        };
      }
      
      case 'search_this_period': {
        const period = match[1].toLowerCase();
        const results = searchByPeriod(period, false);
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📅 Found ${results.length} document${results.length > 1 ? 's' : ''} from this ${period}:`,
            documents: results,
            suggestions: [
              { label: '📅 Last ' + period, command: 'documents from last ' + period },
              { label: '🔍 New search', command: 'search' }
            ]
          };
        }
        return {
          success: true,
          message: `📅 No documents uploaded this ${period} yet.`,
          suggestions: [
            { label: '📅 Last ' + period, command: 'documents from last ' + period },
            { label: '📤 Upload new', command: 'upload document' }
          ]
        };
      }
      
      case 'search_relative_day': {
        const day = match[1].toLowerCase();
        const now = new Date();
        let targetDate;
        
        if (day === 'today') {
          targetDate = now;
        } else {
          targetDate = new Date(now);
          targetDate.setDate(now.getDate() - 1);
        }
        
        const startOfDay = new Date(targetDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(targetDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        const results = documents.filter(doc => {
          if (!doc.created_at) return false;
          const docDate = new Date(doc.created_at);
          return docDate >= startOfDay && docDate <= endOfDay;
        }).slice(0, 8);
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📅 Found ${results.length} document${results.length > 1 ? 's' : ''} uploaded ${day}:`,
            documents: results,
            suggestions: [
              { label: '📅 This week', command: 'documents from this week' },
              { label: '🔍 New search', command: 'search' }
            ]
          };
        }
        return {
          success: true,
          message: `📅 No documents uploaded ${day}.`,
          suggestions: [
            { label: '📅 This week', command: 'documents from this week' },
            { label: '📅 Recent docs', command: 'show recent documents' }
          ]
        };
      }
      
      case 'search_by_status': {
        const status = match[1];
        const results = searchByStatus(status);
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📋 Found ${results.length} ${status} document${results.length > 1 ? 's' : ''}:`,
            documents: results,
            suggestions: [
              { label: '📝 Draft docs', command: 'documents with status draft' },
              { label: '⏳ Pending', command: 'documents with status pending' },
              { label: '✅ Approved', command: 'documents with status approved' }
            ]
          };
        }
        return {
          success: true,
          message: `📋 No ${status} documents found.`,
          suggestions: [
            { label: '📝 All drafts', command: 'documents with status draft' },
            { label: '🔍 Search', command: 'search' }
          ]
        };
      }
      
      case 'search_by_type': {
        const docType = match[1];
        const results = searchByType(docType);
        
        if (results.length > 0) {
          return {
            success: true,
            message: `📄 Found ${results.length} ${docType} document${results.length > 1 ? 's' : ''}:`,
            documents: results,
            suggestions: [
              { label: '📋 Protocols', command: 'all protocol documents' },
              { label: '📝 Consent forms', command: 'all consent documents' },
              { label: '📊 Reports', command: 'all report documents' }
            ]
          };
        }
        return {
          success: true,
          message: `📄 No ${docType} documents found.`,
          suggestions: [
            { label: '📤 Upload', command: 'upload document' },
            { label: '🔍 Search', command: 'search' }
          ]
        };
      }
      
      case 'search_overdue':
      case 'search_due_soon':
      case 'search_pending_requirements': {
        return {
          success: true,
          message: "📋 To view pending document requirements and overdue items, check the **Pending Actions** panel on the dashboard, or I can take you to the document schedule.\n\nThe system tracks required documents based on study timeline milestones (First Patient In, Database Lock, etc.).",
          suggestions: [
            { label: '🏠 Go to Dashboard', command: 'go home' },
            { label: '📊 View Analytics', command: 'go to analytics' },
            { label: '⏳ Pending Actions', command: 'pending actions' }
          ]
        };
      }
      
      case 'show_timeline': {
        return {
          success: true,
          message: "📅 **Study Timeline & Document Schedule**\n\nYour study has milestone-based document requirements:\n\n• **Pre-Study**: Protocol, IB, ICF templates\n• **Site Activation**: IRB approval, contracts, training\n• **Enrollment**: Monitoring & safety reports\n• **Follow-up**: Final monitoring reports\n• **Closeout**: Audit reports, CSR\n\nCheck the **Pending Actions** panel to see what's due!",
          suggestions: [
            { label: '⏳ Pending docs', command: 'what documents are pending' },
            { label: '📊 Analytics', command: 'go to analytics' },
            { label: '🏠 Dashboard', command: 'go home' }
          ]
        };
      }
      
      case 'search_specific_date':
      case 'search_date_range': {
        return {
          success: true,
          message: "📅 For specific date range searches, I recommend using the **Smart Search** panel which has a date picker for precise filtering.\n\nOr try these quick searches:",
          suggestions: [
            { label: '📅 This week', command: 'documents from this week' },
            { label: '📅 This month', command: 'documents from this month' },
            { label: '🔎 Smart Search', command: 'open smart search' }
          ]
        };
      }
      
      case 'help':
        return { 
          success: true, 
          message: "Here's everything I can help with! 🌟\n\n" +
            "🔍 **Search Documents**\n" +
            "• \"search protocol\" - find by name\n" +
            "• \"find consent form\" - search docs\n" +
            "• \"show recent uploads\" - latest docs\n\n" +
            "📅 **Date-Based Search**\n" +
            "• \"docs from January 2024\"\n" +
            "• \"docs from Q1 2024\"\n" +
            "• \"docs uploaded last month\"\n" +
            "• \"docs uploaded this week\"\n\n" +
            "📋 **Status & Type**\n" +
            "• \"approved documents\"\n" +
            "• \"pending documents\"\n" +
            "• \"all protocol documents\"\n\n" +
            "🎨 **Theme & View**\n" +
            "• \"dark mode\" / \"light mode\"\n" +
            "• \"tiles view\" / \"list view\"\n\n" +
            "🧭 **Navigation**\n" +
            "• \"go to analytics\"\n" +
            "• \"go to compliance\"\n\n" +
            "📄 **Documents**\n" +
            "• \"upload document\"\n" +
            "• \"show pending\"\n" +
            "• \"show timeline\"",
          suggestions: SUGGESTION_CHIPS.greeting
        };
      
      default:
        return { 
          success: false, 
          message: "I couldn't process that command. Try 'help' to see what I can do! 🤔",
          suggestions: SUGGESTION_CHIPS.greeting
        };
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!input.trim()) return;

    // Add user message
    setMessages(prev => [...prev, { type: 'user', text: input, timestamp: new Date() }]);
    
    // Process command
    const result = processCommand(input);
    
    // Clear input
    setInput('');
    
    // Add bot response
    addBotMessage(result.message, result.suggestions, result.documents);
  };

  const handleSuggestionClick = (command) => {
    setInput(command);
    // Add user message
    setMessages(prev => [...prev, { type: 'user', text: command, timestamp: new Date() }]);
    
    // Process command
    const result = processCommand(command);
    
    // Clear input
    setInput('');
    
    // Add bot response
    addBotMessage(result.message, result.suggestions, result.documents);
  };

  const handleDocumentClick = (doc) => {
    // Navigate to document or trigger preview
    botEvents.emit('search', doc.name);
    navigate(`/dashboard?view=all&search=${encodeURIComponent(doc.name)}`);
    addBotMessage(`Opening "${doc.name}"... Click on it in the main view to preview! 👁️`, SUGGESTION_CHIPS.documents);
  };

  // Render document card in chat
  const DocumentCard = ({ doc }) => (
    <div 
      onClick={() => handleDocumentClick(doc)}
      className="flex items-center gap-3 p-2 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-red-400 cursor-pointer transition-all hover:shadow-md"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        doc.file_type?.toLowerCase() === 'pdf' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-slate-100 dark:bg-slate-600'
      }`}>
        {doc.file_type?.toLowerCase() === 'pdf' ? (
          <File className="w-5 h-5 text-red-600" />
        ) : ['jpg', 'jpeg', 'png', 'gif'].includes(doc.file_type?.toLowerCase()) ? (
          <FileImage className="w-5 h-5 text-blue-600" />
        ) : (
          <FileText className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{doc.name}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {doc.file_type?.toUpperCase()} • {doc.document_type || 'Document'}
        </p>
      </div>
      <Eye className="w-4 h-4 text-slate-400" />
    </div>
  );

  return (
    <>
      {/* Floating Bot Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen 
            ? 'bg-slate-600 dark:bg-slate-700 rotate-0' 
            : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500'
        }`}
        title="eTMF Vault Assistant"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <div className="relative">
            <Bot className="w-7 h-7 text-white" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
          </div>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-40 right-6 z-50 w-[420px] h-[550px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-rose-600 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">eTMF Vault Assistant</h3>
              <p className="text-white/70 text-xs flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> AI-powered help
              </p>
            </div>
            <Badge className="bg-green-500/20 text-green-100 border-green-400/30 text-xs">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse" />
              Online
            </Badge>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div key={idx}>
                  <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                        msg.type === 'user'
                          ? 'bg-red-600 text-white rounded-br-md'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    </div>
                  </div>
                  
                  {/* Document results */}
                  {msg.documents && msg.documents.length > 0 && (
                    <div className="mt-2 space-y-2 ml-2">
                      {msg.documents.map((doc, docIdx) => (
                        <DocumentCard key={docIdx} doc={doc} />
                      ))}
                    </div>
                  )}
                  
                  {/* Suggestion chips */}
                  {msg.suggestions && msg.type === 'bot' && (
                    <div className="mt-2 flex flex-wrap gap-1 ml-2">
                      {msg.suggestions.map((suggestion, sugIdx) => (
                        <button
                          key={sugIdx}
                          onClick={() => handleSuggestionClick(suggestion.command)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-white dark:bg-slate-700 hover:bg-red-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-full border border-slate-200 dark:border-slate-600 hover:border-red-300 transition-colors"
                        >
                          {suggestion.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 dark:bg-slate-700 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message... (try 'hello' or 'help')"
                className="flex-1 bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 rounded-full"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={!input.trim()}
                className="rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center mt-2">
              💡 Tip: I understand natural language! Just chat with me.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
